import axios from "axios";

/**
 * WordPress REST API 封装
 *
 * 数据源：WordPress 线上站点 https://wp.iepose.com/（已配 CORS：Access-Control-Allow-Origin: https://iepose.com）
 * 接口地址：/wp-json/wp/v2/...
 * 生产环境可直接跨域请求；dev 走 devServer.proxy（vue.config.js 已指向同一线上站点）
 */
let WP_API_BASE =
  process.env.NODE_ENV === "development"
    ? "/wp-json"
    : "https://wp.iepose.com/wp-json";

// 列表每页条数
// 每页 12 条，铺满 3 列 × 4 行（之前 9 条只铺 3 行）
const PER_PAGE = 12;

/**
 * 从附件里挑一个最适合列表卡片显示的变体 URL
 * 卡片 340x187(移动端)/400x220(PC) 显示，2x 视网膜约需 680-800px：
 * 优先 medium_large(768px)，传回原图在高延迟服务器上最拖加载（实测 248KB/4s vs 89KB/1.1s）
 */
function pickCoverUrl(media) {
  const sizes = media.media_details && media.media_details.sizes;
  if (!sizes) {
    return media.source_url || "";
  }
  // 按 轻->稍大 优先级挑选（够用即止，尽量少传字节）
  const prefer = ["medium_large", "large", "medium", "thumbnail"];
  for (let i = 0; i < prefer.length; i++) {
    const size = sizes[prefer[i]];
    if (size && size.source_url) {
      return size.source_url;
    }
  }
  return media.source_url || "";
}

/**
 * 按附件 id 批量取特色图 URL（返回 id -> source_url 映射）
 * 列表接口不带 _embed 提速，特色图单独一次批量请求补齐；媒体接口失败只丢图不影响列表
 */
function getCovers(mediaIds) {
  const validIds = (mediaIds || []).filter((id) => id > 0);
  if (!validIds.length) {
    // 本页文章都无特色图（老数据），直接返回空映射
    return Promise.resolve({});
  }
  return axios
    .get(WP_API_BASE + "/wp/v2/media", {
      // media_details 里带 WP 自动生成的尺寸变体，卡片只需 ~768px，别再下 1536px 原图
      params: { include: validIds.join(","), per_page: 100, _fields: "id,source_url,media_details" },
    })
    .then((res) => {
      const coverMap = {};
      (res.data || []).forEach((media) => {
        coverMap[media.id] = pickCoverUrl(media);
      });
      return coverMap;
    })
    .catch((err) => {
      console.error("特色图批量加载失败", err);
      return {};
    });
}

/**
 * 去掉 HTML 标签，取纯文本摘要
 */
function stripHtml(html) {
  const div = document.createElement("div");
  div.innerHTML = html || "";
  return (div.textContent || "").trim();
}

/**
 * 请求 Wordpress 文章列表
 * @param {Object} params { page, category (分类id), tag (标签id), search (关键词) }
 * @returns {Promise<{list, total, totalPages, page}>}
 */
function getPosts(params = {}) {
  const { page = 1, category, tag, search } = params;
  const query = {
    page,
    per_page: PER_PAGE,
    // 列表只用标题/摘要/日期/正文/特色图 id，字段裁剪 + 不 _embed（_fields 与 _embed 互斥，
    // 且实测 _embed 全量 96KB/4.7s；分类/标签只在详情页用）：实测耗时减半
    _fields: "id,date,title,excerpt,content,featured_media",
    orderby: "date",
    order: "desc",
  };
  if (category) {
    query.categories = category;
  }
  if (tag) {
    query.tags = tag;
  }
  if (search) {
    query.search = search;
  }

  return axios
    .get(WP_API_BASE + "/wp/v2/posts", { params: query })
    .then((res) => {
      const totalPages = parseInt(res.headers["x-wp-totalpages"] || "1", 10);
      const total = parseInt(res.headers["x-wp-total"] || "0", 10);
      // 不带 _embed 时拿不到特色图，改为按 featured_media id 批量取一次（一次请求全部映射）
      const mediaIds = (res.data || []).map((post) => post.featured_media);
      return getCovers(mediaIds).then((coverMap) => {
        const list = (res.data || []).map((post) => normalizePost(post, coverMap));
        return { list, total, totalPages, page };
      });
    });
}

/**
 * 请求单篇文章详情
 * @param {number|string} id 文章 id
 */
function getPost(id) {
  return axios
    .get(WP_API_BASE + "/wp/v2/posts/" + id, { params: { _embed: 1 } })
    .then((res) => normalizePost(res.data));
}

/**
 * 分类列表（资讯页筛选用）
 */
function getCategories() {
  return axios
    .get(WP_API_BASE + "/wp/v2/categories", {
      params: { per_page: 50, hide_empty: 1 },
    })
    .then((res) => res.data || []);
}

/**
 * 获取单个标签信息（用于标签相关文章页的标题）
 */
function getTag(tagId) {
  return axios
    .get(WP_API_BASE + "/wp/v2/tags/" + tagId)
    .then((res) => res.data);
}

/**
 * 把 WP 原始数据规整为前端友好结构
 * @param {Object} post WP 原始文章
 * @param {Object} [coverMap] 列表接口传入的 { attachmentId: source_url } 映射（详情接口走 _embed 不用传）
 */
function normalizePost(post, coverMap) {
  if (!post) {
    return post;
  }
  // 封面图：优先列表接口批量取回的特色图（coverMap），其次 _embed 的 featuredmedia
  let cover = "";
  if (post.featured_media && coverMap && coverMap[post.featured_media]) {
    cover = coverMap[post.featured_media];
  }
  const media = post._embedded && post._embedded["wp:featuredmedia"];
  if (!cover && media && media[0]) {
    cover = media[0].source_url || "";
  }
  // 线上文章的 featured_media 是 Halo 迁移残留 ID，媒体库里没有对应附件；
  // 兜底取正文第一张图做封面（正文图片在 wp-content/uploads 下，可直接访问），取不到再走前端渐变占位
  if (!cover) {
    const contentHtml = (post.content && post.content.rendered) || "";
    const imgMatch = contentHtml.match(/<img[^>]+src=["']([^"']+)["']/i);
    if (imgMatch) {
      cover = imgMatch[1];
    }
  }
  // 分类名称与标签列表（_embed 的 wp:term 是数组的数组，每组对应一种分类法）
  let categoryName = "";
  let tags = [];
  const terms = post._embedded && post._embedded["wp:term"];
  if (terms) {
    terms.forEach((group) => {
      if (!group || !group[0]) {
        return;
      }
      const term = group[0];
      if (term.taxonomy === "category" && term.name) {
        categoryName = term.name;
      } else if (term.taxonomy === "post_tag") {
        group.forEach((tag) => {
          tags.push({ id: tag.id, name: tag.name });
        });
      }
    });
  }
  return {
    id: post.id,
    slug: post.slug,
    title: stripHtml(post.title && post.title.rendered),
    excerpt: stripHtml(post.excerpt && post.excerpt.rendered),
    tags,
    content: (post.content && post.content.rendered) || "",
    // 只取日期部分（WordPress 时间格式为 YYYY-MM-DDTHH:mm:ss）
    date: post.date ? post.date.slice(0, 10) : "",
    cover,
    categoryName,
  };
}

export { WP_API_BASE, PER_PAGE, getPosts, getPost, getCategories, getTag };
